import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../lib/api';
import Spinner from '../../components/ui/Spinner';
import { Select } from '../../components/ui/Kit';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import {
  TRACKER_METRICS,
  addWeeksToMonday,
  buildManualByDay,
  computeTrackerRatios,
  countDaysWithCalls,
  emptyManualDay,
  formatDollars,
  formatWeekRangeLabel,
  getDayValue,
  getMondayOfWeek,
  getWeekDayStrings,
  isFutureDate,
  manualPayloadFromDay,
  sumWeekTotals,
  todayDateKey,
} from '../../lib/kpiTrackerUtils';
import { getActiveDirectoryUsers, useUsersDirectory } from '../../lib/usersDirectory';
import ReadTheRoomSection from './ReadTheRoomSection';
import ChannelSelector from '../../components/Leads/ChannelSelector';
import { CHANNEL_COLORS, CHANNEL_LABELS } from '../../lib/leadMeta';

const { FiChevronLeft, FiChevronRight } = FiIcons;

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const MANAGER_ROLES = new Set(['manager', 'admin', 'super_admin']);

function formatCellValue(metric, value) {
  if (metric.isDollar) return formatDollars(value);
  return String(value ?? 0);
}

function SaveIndicator({ status }) {
  if (status === 'saving') {
    return <span className="block text-[10px] font-normal text-gray-400 mt-0.5">Saving…</span>;
  }
  if (status === 'saved') {
    return <span className="block text-[10px] font-normal text-emerald-600 mt-0.5">Saved</span>;
  }
  if (status === 'error') {
    return <span className="block text-[10px] font-normal text-red-600 mt-0.5">Error</span>;
  }
  return null;
}

export default function Tracker() {
  const { user } = useAuth();
  const isManager = MANAGER_ROLES.has(user?.role);
  const { users: directoryUsers } = useUsersDirectory();
  const [weekStart, setWeekStart] = useState(() => getMondayOfWeek());
  const [selectedAgentId, setSelectedAgentId] = useState('self');
  const [manualLogs, setManualLogs] = useState([]);
  const [autoByDay, setAutoByDay] = useState({});
  const [agencyWeek, setAgencyWeek] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState({});
  const [channelFilter, setChannelFilter] = useState('');
  const [channelSummary, setChannelSummary] = useState(null);
  const saveTimers = useRef({});

  const roster = useMemo(
    () => (isManager ? getActiveDirectoryUsers(directoryUsers) : []),
    [directoryUsers, isManager],
  );

  const dayStrings = useMemo(() => getWeekDayStrings(weekStart), [weekStart]);
  const todayKey = todayDateKey();
  const viewingAllAgents = isManager && selectedAgentId === '__all__';
  const effectiveAgentId =
    selectedAgentId === 'self' || selectedAgentId === '__all__' ? user?.id : selectedAgentId;

  useEffect(() => {
    document.title = 'KPI Tracker — Prosperity Leads';
  }, []);

  const loadWeek = useCallback(async () => {
    if (!user?.agency_id || !user?.id) return;
    setLoading(true);
    try {
      const weekEnd = dayStrings[6];
      const dateRange = { startDate: weekStart, endDate: weekEnd };

      if (viewingAllAgents) {
        const data = await api.kpi.getAgencyWeek(user.agency_id, weekStart);
        setAgencyWeek(data);
        setManualLogs([]);
        setAutoByDay({});
      } else {
        const agentId = effectiveAgentId;
        const channel = channelFilter || null;
        const fetchManual =
          isManager && agentId !== user.id
            ? api.kpi.getWeekForAgent(agentId, user.agency_id, weekStart, { channel })
            : api.kpi.getWeekData(agentId, user.agency_id, weekStart, { channel });
        const [logs, auto, summary] = await Promise.all([
          fetchManual,
          api.kpi.getAutoStats(agentId, user.agency_id, dateRange, { channel }),
          api.kpi.getChannelWeekSummary(agentId, user.agency_id, weekStart),
        ]);
        setManualLogs(logs);
        setAutoByDay(auto);
        setChannelSummary(summary);
        setAgencyWeek(null);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [
    user?.agency_id,
    user?.id,
    weekStart,
    dayStrings,
    effectiveAgentId,
    viewingAllAgents,
    isManager,
    channelFilter,
  ]);

  useEffect(() => {
    void loadWeek();
  }, [loadWeek]);

  const manualByDay = useMemo(
    () => buildManualByDay(manualLogs, { sumByDate: !channelFilter }),
    [manualLogs, channelFilter],
  );
  const canEditManual = effectiveAgentId === user?.id && Boolean(channelFilter);
  const totals = useMemo(
    () => sumWeekTotals(dayStrings, autoByDay, manualByDay),
    [dayStrings, autoByDay, manualByDay],
  );
  const daysWithCalls = useMemo(
    () => countDaysWithCalls(dayStrings, autoByDay),
    [dayStrings, autoByDay],
  );
  const ratios = useMemo(
    () => computeTrackerRatios(totals, daysWithCalls),
    [totals, daysWithCalls],
  );

  const persistManualDay = useCallback(
    async (day, nextManualByDay) => {
      if (!user?.agency_id || !user?.id) return;
      const payload = manualPayloadFromDay(nextManualByDay, day, { channel: channelFilter });
      setSaveStatus((s) => ({ ...s, [day]: 'saving' }));
      try {
        const saved = await api.kpi.upsertDay(user.id, user.agency_id, day, payload);
        setManualLogs((prev) => {
          const rest = prev.filter((r) => r.date !== day);
          return [...rest, saved];
        });
        setSaveStatus((s) => ({ ...s, [day]: 'saved' }));
        if (saveTimers.current[day]) clearTimeout(saveTimers.current[day]);
        saveTimers.current[day] = setTimeout(() => {
          setSaveStatus((s) => {
            const next = { ...s };
            delete next[day];
            return next;
          });
        }, 2000);
      } catch (err) {
        console.error(err);
        setSaveStatus((s) => ({ ...s, [day]: 'error' }));
      }
    },
    [user?.agency_id, user?.id, channelFilter],
  );

  const updateManualLocal = useCallback(
    (day, field, rawValue) => {
      if (isFutureDate(day) || !canEditManual) return;
      const parsed =
        field === 'ap_submitted_dollars'
          ? Math.max(0, parseFloat(rawValue) || 0)
          : Math.max(0, parseInt(rawValue, 10) || 0);
      setManualLogs((prev) => {
        const existing = prev.find((r) => r.date === day);
        const nextDay = { ...(manualByDay[day] ?? emptyManualDay()), [field]: parsed };
        const row = {
          ...(existing ?? { date: day, user_id: user.id }),
          ...nextDay,
          date: day,
        };
        const rest = prev.filter(
          (r) => !(r.date === day && (!channelFilter || r.channel === channelFilter)),
        );
        return [...rest, { ...row, channel: channelFilter }];
      });
    },
    [manualByDay, canEditManual, channelFilter, user.id],
  );

  const saveManualDay = useCallback(
    (day) => {
      if (isFutureDate(day) || !canEditManual) return;
      const nextManualByDay = {
        ...manualByDay,
        [day]: manualByDay[day] ?? emptyManualDay(),
      };
      void persistManualDay(day, nextManualByDay);
    },
    [manualByDay, persistManualDay, canEditManual],
  );

  const agentOptions = useMemo(() => {
    const opts = [{ value: 'self', label: 'Me' }];
    if (isManager) {
      for (const a of roster) {
        if (a.id !== user?.id) {
          opts.push({ value: a.id, label: a.full_name || 'Agent' });
        }
      }
      opts.push({ value: '__all__', label: 'All Agents' });
    }
    return opts;
  }, [roster, isManager, user?.id]);

  const agencySummaryRows = useMemo(() => {
    if (!agencyWeek?.agents) return [];
    return agencyWeek.agents.map((agent) => {
      const manual = buildManualByDay(agent.manualLogs);
      const t = sumWeekTotals(dayStrings, agent.autoByDay, manual);
      return { ...agent, totals: t };
    });
  }, [agencyWeek, dayStrings]);

  if (!user) return null;

  return (
    <div className="p-4 md:p-8 max-w-[1600px] mx-auto space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Weekly KPI Tracker</h1>
          <p className="text-sm text-gray-500 mt-1">
            Monday–Sunday · Auto stats from calls &amp; appointments
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
          {isManager ? (
            <Select
              value={selectedAgentId}
              onChange={(e) => setSelectedAgentId(e.target.value)}
              className="min-w-[180px]"
              options={agentOptions}
            />
          ) : null}
          <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-2 py-1.5 shadow-sm">
            <button
              type="button"
              onClick={() => setWeekStart((w) => addWeeksToMonday(w, -1))}
              className="p-2 rounded-md hover:bg-gray-100 text-gray-600"
              aria-label="Previous week"
            >
              <SafeIcon icon={FiChevronLeft} />
            </button>
            <span className="text-sm font-medium text-gray-800 whitespace-nowrap px-2">
              {formatWeekRangeLabel(weekStart)}
            </span>
            <button
              type="button"
              onClick={() => setWeekStart((w) => addWeeksToMonday(w, 1))}
              className="p-2 rounded-md hover:bg-gray-100 text-gray-600"
              aria-label="Next week"
            >
              <SafeIcon icon={FiChevronRight} />
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Spinner size="lg" className="text-primary" />
        </div>
      ) : viewingAllAgents ? (
        <AgencySummaryTable
          rows={agencySummaryRows}
          metrics={TRACKER_METRICS}
          onSelectAgent={(id) => setSelectedAgentId(id)}
        />
      ) : (
        <>
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Channel</span>
              <ChannelSelector value={channelFilter || null} onChange={(ch) => setChannelFilter(ch || '')} allowEmpty />
            </div>
            {!channelFilter && (
              <p className="text-xs text-gray-500">
                Select LD, IM, or AM to edit manual KPI entries for that channel. Auto-stats filter when a channel is selected.
              </p>
            )}
          </div>

          {channelSummary && (
            <ChannelSummaryRow summary={channelSummary} />
          )}

          <DailyActivityGrid
            metrics={TRACKER_METRICS}
            dayStrings={dayStrings}
            todayKey={todayKey}
            autoByDay={autoByDay}
            manualByDay={manualByDay}
            totals={totals}
            canEdit={canEditManual}
            saveStatus={saveStatus}
            onManualInput={updateManualLocal}
            onManualBlur={saveManualDay}
          />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <WeeklyTotalsCard metrics={TRACKER_METRICS} totals={totals} />
            <RatiosCard ratios={ratios} />
          </div>
          <ReadTheRoomSection
            user={user}
            weekStart={weekStart}
            setWeekStart={setWeekStart}
            selectedAgentId={selectedAgentId}
            setSelectedAgentId={setSelectedAgentId}
            isManager={isManager}
            agentOptions={agentOptions}
            viewingAllAgents={viewingAllAgents}
            channelFilter={channelFilter}
          />
        </>
      )}
    </div>
  );
}

function ChannelSummaryRow({ summary }) {
  const cards = [
    {
      id: 'LD',
      title: CHANNEL_LABELS.LD,
      color: CHANNEL_COLORS.LD,
      lines: [
        `${summary.LD?.calls ?? 0} calls`,
        `${summary.LD?.apps ?? 0} apps`,
        `$${Number(summary.LD?.apDollars ?? 0).toLocaleString()} AP`,
      ],
    },
    {
      id: 'IM',
      title: CHANNEL_LABELS.IM,
      color: CHANNEL_COLORS.IM,
      lines: [
        `${summary.IM?.presentations ?? 0} presentations`,
        `${summary.IM?.recruits ?? 0} recruits`,
        `${summary.IM?.events ?? 0} events`,
      ],
    },
    {
      id: 'AM',
      title: CHANNEL_LABELS.AM,
      color: CHANNEL_COLORS.AM,
      lines: [
        `${summary.AM?.meetings ?? 0} meetings`,
        `${summary.AM?.referrals ?? 0} referrals`,
        `${summary.AM?.partnerships ?? 0} partnerships`,
      ],
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      {cards.map((card) => (
        <div
          key={card.id}
          className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
          style={{ borderTopWidth: 4, borderTopColor: card.color }}
        >
          <p className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-2">{card.title}</p>
          <ul className="space-y-1">
            {card.lines.map((line) => (
              <li key={line} className="text-sm text-gray-800 tabular-nums">
                {line}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function DailyActivityGrid({
  metrics,
  dayStrings,
  todayKey,
  autoByDay,
  manualByDay,
  totals,
  canEdit,
  saveStatus,
  onManualInput,
  onManualBlur,
}) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 bg-gray-50">
            <th className="text-left py-3 px-4 font-semibold text-gray-700 sticky left-0 bg-gray-50 z-10 min-w-[160px]">
              Metric
            </th>
            {dayStrings.map((day, i) => (
              <th
                key={day}
                className={`py-3 px-2 text-center font-semibold min-w-[72px] ${
                  day === todayKey ? 'bg-sky-50 text-sky-900' : 'text-gray-600'
                }`}
              >
                <span className="block">{DAY_LABELS[i]}</span>
                <span className="block text-[10px] font-normal text-gray-400">
                  {format(parseISO(`${day}T12:00:00`), 'M/d')}
                </span>
                {day === todayKey && canEdit ? (
                  <SaveIndicator status={saveStatus[day]} />
                ) : null}
              </th>
            ))}
            <th className="py-3 px-3 text-center font-semibold text-gray-700 bg-gray-50">Total</th>
          </tr>
        </thead>
        <tbody>
          {metrics.map((metric) => (
            <tr key={metric.key} className="border-b border-gray-100 last:border-0">
              <td
                className={`py-2 px-4 font-medium sticky left-0 bg-white z-10 ${
                  metric.type === 'auto' ? 'text-gray-500' : 'text-gray-800'
                }`}
              >
                {metric.label}
              </td>
              {dayStrings.map((day) => {
                const future = isFutureDate(day);
                const value = getDayValue(metric.key, day, autoByDay, manualByDay, future);
                const isToday = day === todayKey;
                return (
                  <td
                    key={day}
                    className={`py-1.5 px-2 text-center ${isToday ? 'bg-sky-50/80' : ''}`}
                  >
                    {metric.type === 'auto' ? (
                      <span className="text-gray-500 tabular-nums">
                        {formatCellValue(metric, value)}
                      </span>
                    ) : (
                      <input
                        type="number"
                        min={0}
                        step={metric.isDollar ? '0.01' : '1'}
                        disabled={future || !canEdit}
                        value={future ? 0 : manualByDay[day]?.[metric.key] ?? 0}
                        onChange={(e) => onManualInput(day, metric.key, e.target.value)}
                        onBlur={() => onManualBlur(day)}
                        className={`w-16 text-center border rounded px-1 py-1 tabular-nums text-sm ${
                          future || !canEdit
                            ? 'bg-gray-50 text-gray-400 border-gray-100'
                            : 'border-gray-200 focus:ring-1 focus:ring-primary focus:border-primary'
                        }`}
                      />
                    )}
                  </td>
                );
              })}
              <td className="py-2 px-3 text-center font-semibold text-gray-900 tabular-nums bg-gray-50/50">
                {formatCellValue(metric, totals[metric.key])}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function WeeklyTotalsCard({ metrics, totals }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Weekly Totals</h2>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="text-left py-2 font-medium text-gray-600">Metric</th>
            <th className="text-right py-2 font-medium text-gray-600">Total</th>
          </tr>
        </thead>
        <tbody>
          {metrics.map((m) => (
            <tr key={m.key} className="border-b border-gray-50 last:border-0">
              <td className={`py-2 ${m.type === 'auto' ? 'text-gray-500' : 'text-gray-800'}`}>
                {m.label}
              </td>
              <td className="py-2 text-right font-semibold tabular-nums">
                {formatCellValue(m, totals[m.key])}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RatiosCard({ ratios }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Ratios &amp; Averages</h2>
      <dl className="space-y-2 text-sm">
        {ratios.map((r) => (
          <div
            key={r.label}
            className="flex justify-between gap-4 py-1 border-b border-gray-50 last:border-0"
          >
            <dt className="text-gray-600">{r.label}</dt>
            <dd className="font-semibold text-gray-900 tabular-nums">{r.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function AgencySummaryTable({ rows, metrics, onSelectAgent }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-x-auto">
      <p className="text-sm text-gray-500 px-4 pt-4">
        Click an agent row to open their detailed weekly view.
      </p>
      <table className="min-w-full text-sm mt-2">
        <thead>
          <tr className="border-b border-gray-200 bg-gray-50">
            <th className="text-left py-3 px-4 font-semibold text-gray-700 sticky left-0 bg-gray-50">
              Agent
            </th>
            {metrics.map((m) => (
              <th
                key={m.key}
                className="py-3 px-2 text-right font-semibold text-gray-600 whitespace-nowrap"
              >
                {m.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.userId}
              onClick={() => onSelectAgent(row.userId)}
              className="border-b border-gray-100 hover:bg-sky-50/50 cursor-pointer transition-colors"
            >
              <td className="py-3 px-4 font-medium text-primary sticky left-0 bg-white">
                {row.fullName}
              </td>
              {metrics.map((m) => (
                <td key={m.key} className="py-3 px-2 text-right tabular-nums text-gray-800">
                  {formatCellValue(m, row.totals[m.key])}
                </td>
              ))}
            </tr>
          ))}
          {rows.length === 0 ? (
            <tr>
              <td colSpan={metrics.length + 1} className="py-8 text-center text-gray-400">
                No active agents found.
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}
